// @flow

import React from 'react';
import * as Yup from 'yup';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import AddShipmentItemMutation from 'main-app/mutations/AddShipmentItem';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  SelectItemField,
} from 'shared/components/form';

type Props = {
  onClose: () => void,
  shipmentId: string,
  existingItemIds: Array<string>,
};

const AddShipmentItemModal = ({
  onClose,
  shipmentId,
  existingItemIds,
}: Props) => {
  return (
    <Modal maxWidth={700}>
      <Formik
        initialValues={{
          item: null,
          quantity: '',
          description: '',
        }}
        validationSchema={Yup.object().shape({
          item: Yup.object()
            .nullable()
            .required(i18n.t('Item is required')),
          quantity: Yup.number()
            .moreThan(0, i18n.t('Enter a value greater than 0'))
            .required(i18n.t('Quantity is required')),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);

          try {
            await AddShipmentItemMutation.commit({
              variables: {
                input: {
                  shipmentId,
                  itemId: values.item.value,
                  quantity: +values.quantity,
                  description: values.description || null,
                },
              },
            });

            onClose();
            Actions.alertNotification(
              i18n.t('Item added to shipment'),
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({ errors, values, isValid, isSubmitting }) => (
          <Form>
            <ModalHeader
              header={i18n.t('Add Item to Shipment')}
              onClose={onClose}
            />
            <ModalBody withPadding>
              <SelectItemField
                name="item"
                label={i18n.t('Item')}
                placeholder={i18n.t('Search Item or Part #')}
                error={errors.item}
                excludeIds={existingItemIds}
              />
              <FieldGroup
                name="quantity"
                type="number"
                label={i18n.t('Quantity')}
                placeholder={i18n.t('0')}
                error={errors.quantity}
                extendedLabel={values.item?.data?.quantityUOM?.symbol}
              />
              <FieldGroup
                name="description"
                label={i18n.t('Description (Optional)')}
                placeholder={i18n.t('Line item description')}
                error={errors.description}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
              >
                {i18n.t('Add Item')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

export default AddShipmentItemModal;
